import { toast } from "@/components/podkit/toasts/use-toast";
import { TierCard, TierCardAction, TierPill } from "@/components/tiering/TierCard";
import { CORE_WAITLIST_JOINED, ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useGetAccount } from "@/queries/account-queries";
import { CHANGE_PLAN_QUERY_KEY } from "@/routes/settings/organization/billing/Plan";
import { formatError } from "@/utils/errors";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { Building2, UserIcon, UsersIcon } from "lucide-react";
import { type FC, useCallback } from "react";

export type TierOptionsProps = {
    /**
     * Current organization tier
     */
    currentTier?: OrganizationTier;
};

/**
 * TierOptions component for displaying available tier options
 */
export const TierOptions: FC<TierOptionsProps> = ({ currentTier = OrganizationTier.FREE }) => {
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();

    const { value: waitlistJoinedValue, setValueAsync: setWaitlistJoinedValue } =
        usePreferenceValue(CORE_WAITLIST_JOINED);
    const { value: enterpriseTrialRequestedValue, setValueAsync: setEnterpriseTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const waitlistJoined = waitlistJoinedValue === "true";

    const enterpriseTrialRequested = enterpriseTrialRequestedValue === "true";

    const onJoinCoreWaitlist = useCallback(async () => {
        try {
            segmentTrack("waitlist_joined", {
                feature: "Gitpod Core (pricing)",
                provider: "core",
                type: "plan",
                email: account?.email,
            });
            await setWaitlistJoinedValue("true");
            toast({
                title: "You're on the waitlist!",
                description: "We will let you know once the Core tier is available.",
            });
        } catch (error) {
            toast({
                title: "Failed to join waitlist",
                description: formatError(error),
            });
        }
    }, [account?.email, segmentTrack, setWaitlistJoinedValue]);
    const onEnterpriseTrialRequest = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setEnterpriseTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "Our team is working on your request and will be in touch soon.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: formatError(error),
            });
        }
    }, [account?.email, segmentTrack, setEnterpriseTrialRequestedValue]);

    const isFreeTier = currentTier === OrganizationTier.FREE || currentTier === OrganizationTier.FREE_ONA;
    const isCoreTier = currentTier === OrganizationTier.CORE;
    const isEnterpriseTier = currentTier === OrganizationTier.ENTERPRISE;

    const showCoreTierAvailable = currentTier === OrganizationTier.FREE_ONA || currentTier === OrganizationTier.CORE;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
                <TierCard
                    title="Free"
                    description="Get started"
                    icon={<UserIcon className="size-4" />}
                    statusPill={
                        isFreeTier ? (
                            <TierPill className="bg-brand-background text-brand-foreground/90">Active</TierPill>
                        ) : undefined
                    }
                    isActive={isFreeTier}
                    actions={<TierCardAction href="https://ona.com/pricing">Compare tiers</TierCardAction>}
                />

                <TierCard
                    title="Core"
                    description="Ideal for individual users and teams"
                    icon={<UsersIcon className="size-4" />}
                    statusPill={
                        isCoreTier ? (
                            <TierPill className="bg-brand-background text-brand-foreground/90">Active</TierPill>
                        ) : undefined
                    }
                    isActive={isCoreTier}
                    actions={
                        !showCoreTierAvailable && !isEnterpriseTier ? (
                            <TierCardAction
                                onClick={onJoinCoreWaitlist}
                                disabled={waitlistJoined}
                                data-tracking-id="join-core-waitlist-tier-options"
                            >
                                {waitlistJoined ? "Joined waitlist" : "Join waitlist"}
                            </TierCardAction>
                        ) : isCoreTier ? (
                            <TierCardAction to={"/settings/billing"}>Manage billing</TierCardAction>
                        ) : (
                            !isEnterpriseTier && (
                                <TierCardAction to={`/settings/billing?${CHANGE_PLAN_QUERY_KEY}=true`}>
                                    Upgrade
                                </TierCardAction>
                            )
                        )
                    }
                />

                <TierCard
                    title="Enterprise"
                    description="Maximum security, compliance, and full control"
                    icon={<Building2 className="size-4" />}
                    isActive={currentTier === OrganizationTier.ENTERPRISE}
                    statusPill={
                        currentTier === OrganizationTier.ENTERPRISE ? (
                            <TierPill className="bg-brand-background text-brand-foreground/90">Active</TierPill>
                        ) : undefined
                    }
                    actions={
                        currentTier === OrganizationTier.ENTERPRISE ? undefined : (
                            <TierCardAction
                                onClick={onEnterpriseTrialRequest}
                                disabled={enterpriseTrialRequested}
                                data-tracking-id="request-enterprise-trial-tier-options"
                            >
                                {enterpriseTrialRequested ? "Requested free trial" : "Request free trial"}
                            </TierCardAction>
                        )
                    }
                />
            </div>
        </div>
    );
};
